import { useState, useEffect, useCallback } from 'react';
import nocobaseService from '../services/nocobaseService';

// Hooks for fetching data from NocoBase
// All services now use NocoBase only

// Hook for fetching insights
export const useInsights = (options = {}) => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // DISABLED: Insights fetching is not currently active
    setLoading(false);
  }, [JSON.stringify(options)]);

  return { insights, loading, error, refetch: () => {} };
};

// Hook for fetching team members
// Uses NocoBase only
export const useTeamMembers = (activeOnly = true, language = 'en') => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('NocoBase');

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching team members from NocoBase...');
      const nocobaseData = await nocobaseService.getTeamMembers(activeOnly, language);
      
      if (nocobaseData && nocobaseData.length > 0) {
        console.log(`✅ Successfully fetched ${nocobaseData.length} team member(s) from NocoBase`);
        setTeamMembers(nocobaseData);
        setSource('NocoBase');
      } else {
        console.log('⚠️ NocoBase returned empty data for team members');
        setTeamMembers([]);
        setSource('NocoBase');
      }
    } catch (err) {
      console.error('❌ Failed to fetch team members from NocoBase:', err);
      setError(err);
      setTeamMembers([]);
      setSource('NocoBase');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, [activeOnly, language]);

  return { teamMembers, loading, error, source, refetch: fetchTeamMembers };
};

// Hook for fetching services
// Uses NocoBase only
export const useServices = (liveOnly = true, language = 'en') => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('NocoBase');

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching services from NocoBase...');
      const nocobaseData = await nocobaseService.getServices(liveOnly, language);
      
      if (nocobaseData && nocobaseData.length > 0) {
        console.log(`✅ Successfully fetched ${nocobaseData.length} service(s) from NocoBase`);
        setServices(nocobaseData);
        setSource('NocoBase');
      } else {
        console.log('⚠️ NocoBase returned empty data for services');
        setServices([]);
        setSource('NocoBase');
      }
    } catch (err) {
      console.error('❌ Failed to fetch services from NocoBase:', err);
      setError(err);
      setServices([]);
      setSource('NocoBase');
    } finally {
      setLoading(false);
    }
  }, [liveOnly, language]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  return { services, loading, error, source, refetch: fetchServices };
};

// Hook for fetching subservices
// Uses NocoBase only
export const useSubservices = (serviceId = null, liveOnly = true, language = 'en') => {
  const [subservices, setSubservices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('NocoBase');

  const fetchSubservices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 Fetching subservices from NocoBase...');
      const nocobaseData = await nocobaseService.getSubservices(serviceId, liveOnly, language);
      
      if (nocobaseData && nocobaseData.length > 0) {
        console.log(`✅ Successfully fetched ${nocobaseData.length} subservice(s) from NocoBase`);
        setSubservices(nocobaseData);
        setSource('NocoBase');
      } else {
        console.log('⚠️ NocoBase returned empty data for subservices');
        setSubservices([]);
        setSource('NocoBase');
      }
    } catch (err) {
      console.error('❌ Failed to fetch subservices from NocoBase:', err);
      setError(err);
      setSubservices([]);
      setSource('NocoBase');
    } finally {
      setLoading(false);
    }
  }, [serviceId, liveOnly, language]);

  useEffect(() => {
    fetchSubservices();
  }, [fetchSubservices]);

  return { subservices, loading, error, source, refetch: fetchSubservices };
};

// Hook for fetching hero banners
// Uses NocoBase only (NocoDB connection removed)
export const useHeroBanners = (activeOnly = true, language = 'en') => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [source, setSource] = useState('NocoBase');

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching hero banners from NocoBase...');
        const nocobaseData = await nocobaseService.getHeroBanners(activeOnly, language);
        
        if (nocobaseData && nocobaseData.length > 0) {
          console.log(`✅ Successfully fetched ${nocobaseData.length} banner(s) from NocoBase`);
          setBanners(nocobaseData);
          setSource('NocoBase');
        } else {
          console.log('⚠️ NocoBase returned empty data');
          setBanners([]);
          setSource('NocoBase');
        }
      } catch (err) {
        console.error('❌ Failed to fetch banners from NocoBase:', err);
        setBanners([]);
        setError(err);
        setSource('NocoBase');
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, [activeOnly, language]);

  return { banners, loading, error, source, refetch: () => {
    const fetchBanners = async () => {
      try {
        setLoading(true);
        setError(null);
        const nocobaseData = await nocobaseService.getHeroBanners(activeOnly, language);
        if (nocobaseData && nocobaseData.length > 0) {
          setBanners(nocobaseData);
          setSource('NocoBase');
        } else {
          setBanners([]);
          setSource('NocoBase');
        }
      } catch (err) {
        setError(err);
        setBanners([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBanners();
  }};
};

// Hook for fetching page content
export const usePageContent = (pageName) => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // DISABLED: Only hero is active for testing
    setLoading(false);
  }, [pageName]);

  return { pageContent, loading, error, refetch: () => {} };
};

// Hook for fetching website settings
export const useSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // DISABLED: Only hero is active for testing
    setLoading(false);
  }, []);

  return { settings, loading, error, refetch: () => {} };
};

// Hook for submitting contact form
export const useContactSubmission = () => {
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const submitContact = async (contactData) => {
    // DISABLED: Only hero is active for testing
    console.log('Contact submission disabled in testing mode');
    return false;
  };

  const reset = () => {
    setSuccess(false);
    setError(null);
  };

  return { submitContact, submitting, success, error, reset };
};
